import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Trophy, ChevronRight, ArrowUp, ArrowDown, Minus, Loader2, Utensils } from "lucide-react";

const SCOPES = [
  { key: "city", label: "City" },
  { key: "regional", label: "Regional" },
  { key: "state", label: "State" },
];

export default function TruckLeaderboard({ compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState("city");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { scope };
        // Try to get GPS
        try {
          const pos = await new Promise((res, rej) =>
            navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 })
          );
          params.lat = pos.coords.latitude;
          params.lng = pos.coords.longitude;
        } catch { /* no GPS */ }
        const res = await axios.get(`${API}/trucks/leaderboard`, { params });
        setData(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [scope]);

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 text-[#F59E0B] animate-spin" />
      </div>
    );
  }

  const trucks = data?.trucks || [];
  const displayTrucks = compact ? trucks.slice(0, 3) : trucks;

  return (
    <div data-testid="truck-leaderboard">
      {/* Scope tabs */}
      {!compact && (
        <div className="flex gap-1 mb-3 bg-slate-100 rounded-lg p-0.5" data-testid="leaderboard-tabs">
          {SCOPES.map(s => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`flex-1 text-xs font-bold py-2 rounded-md transition ${
                scope === s.key
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-400"
              }`}
              data-testid={`scope-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Truck list */}
      {displayTrucks.length > 0 ? (
        <div className="space-y-2">
          {displayTrucks.map((truck, i) => (
            <button
              key={truck.business_id}
              onClick={() => navigate(`/truck/${truck.business_id}`)}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 hover:border-amber-200 transition text-left"
              data-testid={`leaderboard-entry-${i}`}
            >
              {/* Rank */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${
                truck.rank === 1 ? "bg-amber-400 text-white" :
                truck.rank === 2 ? "bg-slate-300 text-white" :
                truck.rank === 3 ? "bg-amber-700 text-white" :
                "bg-slate-100 text-slate-500"
              }`}>
                {truck.rank}
              </div>

              {/* Photo */}
              <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                {truck.photo_url || truck.logo_url ? (
                  <img src={truck.photo_url || truck.logo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Utensils className="w-4 h-4 text-slate-300" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">{truck.name}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  {(truck.cuisine_tags || []).slice(0, 2).map(c => (
                    <span key={c} className="text-[9px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
                {truck.key_stat && (
                  <p className="text-[9px] text-slate-400 mt-0.5 truncate">{truck.key_stat}</p>
                )}
              </div>

              {/* Score + rank change */}
              <div className="text-right shrink-0">
                <p className="text-sm font-black text-slate-800">{truck.score}</p>
                {truck.rank_change !== null && truck.rank_change !== undefined && (
                  <div className={`flex items-center justify-end gap-0.5 text-[10px] font-bold ${
                    truck.rank_change > 0 ? "text-green-500" :
                    truck.rank_change < 0 ? "text-red-500" :
                    "text-slate-300"
                  }`}>
                    {truck.rank_change > 0 ? <ArrowUp className="w-3 h-3" /> :
                     truck.rank_change < 0 ? <ArrowDown className="w-3 h-3" /> :
                     <Minus className="w-3 h-3" />}
                    {truck.rank_change !== 0 ? Math.abs(truck.rank_change) : "—"}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-6">
          <Trophy className="w-8 h-8 text-slate-200 mx-auto mb-2" />
          <p className="text-xs text-slate-400">No trucks ranked in this area yet</p>
        </div>
      )}

      {compact && trucks.length > 0 && (
        <button
          onClick={() => navigate("/truck-leaderboard")}
          className="w-full mt-2 text-center text-xs font-bold text-[#F59E0B] py-2"
          data-testid="see-full-leaderboard"
        >
          See Full Leaderboard <ChevronRight className="w-3 h-3 inline" />
        </button>
      )}
    </div>
  );
}
